import { c as _c } from "react/compiler-runtime";
import { Fieldset, Heading, LayoutStack, Tooltip, TooltipTrigger } from 'design-system/client';
import { useTranslations } from 'next-intl';
import { ChangeEventHandler, FocusEventHandler, useCallback } from 'react';
import styles from './YoutubeFields.module.css';
import DonationFormField from '@/donationPages/_dependencies/components/DonationFormField';
import { useDonationFormConfig, useDonationFormContext } from '@/donationPages/_dependencies/helpers';
import FormInput from '@/form/FormInput';
import { getHHmmssTimestampFromSeconds } from '@/helpers/utils';
import I18nHtml from '@/components/I18nHtml';
function tryToFixTimestamp(timestamp: string) {
  let fixedTimestamp = timestamp;
  if (fixedTimestamp.match(/^[0-9]+(:[0-9]+){1,2}$/)) {
    const timestampArray = fixedTimestamp.split(':');
    fixedTimestamp = timestampArray.map(e => e.match(/^[0-9]{1}$/) ? '0' + e : e).join(':');
  }
  if (fixedTimestamp.match(/^[0-9]{2}:[0-9]{2}$/)) {
    return '00:' + fixedTimestamp;
  }
  return fixedTimestamp;
}
function getSecondsFromTParam(tParamValue: string) {
  if (tParamValue.match(/^[0-9]+$/)) return Number(tParamValue);
  if (!tParamValue.match(/^([0-9]+[hms])+$/)) return null;
  let totalSeconds = 0;
  tParamValue.split(/(?<=[^0-9])/).forEach(element => {
    if (element.match(/^[0-9]+s$/)) totalSeconds += Number(element.slice(0, -1));
    if (element.match(/^[0-9]+m$/)) totalSeconds += Number(element.slice(0, -1)) * 60;
    if (element.match(/^[0-9]+h$/)) totalSeconds += Number(element.slice(0, -1)) * 3600;
  });
  return totalSeconds;
}
function getParsedUrlWithStartTime(url: string) {
  try {
    var parsedUrl = new URL(url);
  } catch {
    return [null, null] as const;
  }
  const params = parsedUrl.searchParams;
  const tParamValue = params.get('t');
  if (!tParamValue) return [parsedUrl.toString(), null] as const;
  const totalSeconds = getSecondsFromTParam(tParamValue);
  if (!totalSeconds) return [parsedUrl.toString(), null] as const;
  params.delete('t');
  return [parsedUrl.toString(), totalSeconds] as const;
}
function YoutubeFields() {
  const $ = _c(40);
  const t = useTranslations("nextjs.donate");
  const {
    showStreamingShareableMedia
  } = useDonationFormConfig();
  const {
    setValue
  } = useDonationFormContext();
  let t0;
  if ($[0] !== setValue) {
    t0 = value => {
      setValue("video_start_time", value, {
        shouldDirty: true,
        shouldValidate: true
      });
    };
    $[0] = setValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setVideoStartTime = t0;
  let t1;
  if ($[2] !== setVideoStartTime) {
    t1 = t2 => {
      const {
        target
      } = t2;
      const url = target.value;
      if (!url) {
        setVideoStartTime("00:00:00");
        return;
      }
      const [parsedUrl, startTimeSeconds] = getParsedUrlWithStartTime(url);
      if (!startTimeSeconds) {
        return;
      }
      target.value = parsedUrl;
      const videoStartTime = getHHmmssTimestampFromSeconds(startTimeSeconds);
      setVideoStartTime(videoStartTime);
    };
    $[2] = setVideoStartTime;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onVideoLinkChange = t1;
  let t2;
  if ($[4] !== setVideoStartTime) {
    t2 = t3 => {
      const {
        target: target_0
      } = t3;
      const userInput = target_0.value;
      if (!userInput) {
        setVideoStartTime("00:00:00");
        return;
      }
      if (userInput.match(/^[0-9]{1,6}$/)) {
        setVideoStartTime(getHHmmssTimestampFromSeconds(Number(userInput)));
      }
      if (!userInput.match(/^[0-9]{2}:[0-9]{2}:[0-9]{2}$/)) {
        const fixedTimestamp = tryToFixTimestamp(userInput);
        if (fixedTimestamp !== userInput) {
          setVideoStartTime(fixedTimestamp);
        }
      }
    };
    $[4] = setVideoStartTime;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onVideoStartTimeInputLeave = t2;
  if (!showStreamingShareableMedia) {
    return null;
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      space: "400"
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("youtube_fields.headline");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Heading level="heading-xs" className={styles.youtubeHeading}>{t4}</Heading>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <I18nHtml i18nKey="donate.attributes.youtube_disclaimer" options={{
      target: "_blank",
      href: "http://www.google.com/policies/privacy",
      target1: "_blank",
      href1: "https://www.youtube.com/t/terms"
    }} as="p" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("youtube_fields.video_link_hint");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("attributes.video_link");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("attributes.video_tooltip");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <TooltipTrigger type="information" title="" />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t9) {
    t11 = <Tooltip content={t9}>{t10}</Tooltip>;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t8) {
    t12 = <span className={styles.videoUrlLabel}>{t8}{t11}</span>;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== onVideoLinkChange || $[25] !== t12 || $[26] !== t7) {
    t13 = <DonationFormField description={t7} name="video_link" type="url" as={FormInput} label={t12} onChange={onVideoLinkChange} />;
    $[24] = onVideoLinkChange;
    $[25] = t12;
    $[26] = t7;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("youtube_fields.video_start_time_hint");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t) {
    t15 = t("attributes.video_start_time");
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== onVideoStartTimeInputLeave || $[33] !== t14 || $[34] !== t15) {
    t16 = <DonationFormField name="video_start_time" description={t14} as={FormInput} label={t15} defaultValue="00:00:00" placeholder="hh:mm:ss" onBlur={onVideoStartTimeInputLeave} />;
    $[32] = onVideoStartTimeInputLeave;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t13 || $[37] !== t16 || $[38] !== t5) {
    t17 = <LayoutStack><Fieldset className={styles.youtubeFieldset} layoutProps={t3} legend={t5} layout="vertical">{t6}{t13}{t16}</Fieldset></LayoutStack>;
    $[36] = t13;
    $[37] = t16;
    $[38] = t5;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}
export default YoutubeFields;